import React from 'react';
import {
  PensionInputs,
  IndexInputs,
  CalculateButton,
  CalculationResults,
  ComparisonChart,
  ComparisonTable,
  RetirementProjection,
  DisclaimerWarning,
  Tooltip,
} from '../Components';

import { usePensionCalculator, usePageMeta } from '../Hooks';

const Calculator: React.FC = () => {
  usePageMeta(
    'UK Pension vs Index Fund Calculator | Free Retirement Planning Tool',
    'Compare UK pension schemes against Stocks & Shares ISA index fund investing. Calculate compound growth, tax relief, employer contributions and retirement income. Free tool for UK investors.'
  );

  const {
    pensionInputs,
    setPensionInputs,
    indexInputs,
    setIndexInputs,
    inflationRate,
    setInflationRate,
    results,
    loading,
    error,
    handleCalculate,
    validation,
  } = usePensionCalculator();

  return (
    <>
      <h1>Pension vs Index Fund Investment</h1>
      <p className="subtitle">
        Flexible comparison with separate controls for each investment type
      </p>

      <div className="controls-wrapper">
        <PensionInputs
          inputs={pensionInputs}
          onChange={setPensionInputs}
          getFieldError={validation.getFieldError}
          hasFieldError={validation.hasFieldError}
        />
        <IndexInputs
          inputs={indexInputs}
          onChange={setIndexInputs}
          getFieldError={validation.getFieldError}
          hasFieldError={validation.hasFieldError}
        />
      </div>

      <div className="inflation-row">
        <label htmlFor="inflation-rate" className="inflation-label">
          Assumed Inflation Rate (%)
          <Tooltip content="Used to show what your projected returns are worth in today's money. The UK Bank of England target is 2%. Long-run UK average is around 2.5%. Higher inflation erodes purchasing power more." />
        </label>
        <input
          id="inflation-rate"
          type="number"
          className="inflation-input"
          value={inflationRate}
          onChange={e => setInflationRate(Number(e.target.value))}
          step={0.1}
          min={0}
          max={10}
        />
      </div>

      <CalculateButton
        loading={loading}
        onClick={handleCalculate}
        error={error}
        disabled={!validation.isValid}
        validationErrors={validation.allErrors.length}
      />

      {validation.allErrors.length > 0 && (
        <div className="validation-errors-summary">
          <h4>⚠️ Please fix these issues:</h4>
          <ul>
            {validation.allErrors.map((error, index) => (
              <li key={index}>{error.message}</li>
            ))}
          </ul>
        </div>
      )}

      {validation.serverErrors.length > 0 && (
        <div className="server-validation-errors">
          <h4>🚫 Server Validation Errors:</h4>
          <ul>
            {validation.serverErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {results && (
        <>
          <CalculationResults results={results} inflationRate={inflationRate} />
          <ComparisonChart
            results={results}
            pensionInputs={pensionInputs}
            indexInputs={indexInputs}
            inflationRate={inflationRate}
          />
          <RetirementProjection
            projection={results.retirementProjection}
            pensionInputs={pensionInputs}
            indexInputs={indexInputs}
            inflationRate={inflationRate}
          />
        </>
      )}

      <ComparisonTable />
      <DisclaimerWarning />
    </>
  );
};

export default Calculator;